/**
 * AbstractPiniaModel - Bridge between Model classes and Pinia store
 * 
 * This class extends AbstractModel to provide Pinia-specific functionality
 * while maintaining the existing model API pattern.
 * 
 * MIGRATION NOTES:
 * - Use this as a parent class for models migrating from Vuex to Pinia
 * - Models using Pinia are NOT part of VuexPersistence - they manage their own persistence
 * - Coexists with AbstractModel: some models can use Vuex, others can use Pinia
 * 
 * USAGE:
 * 1. Create a Pinia store using defineStore() (see pinia/stores/subscriptions.js for example)
 * 2. Create a model that extends AbstractPiniaModel
 * 3. Implement piniaStore() to return your Pinia store instance
 * 4. Call actions/access state through piniaStore() instead of getters()/dispatch()
 * 
 * DIFFERENCES FROM VUEX:
 * - No mutations: directly modify state through actions
 * - No getters pattern: access reactive refs directly (e.g., store.allSubscriptions)
 * - Better TypeScript support and smaller bundle size
 * - Composition API style (functions instead of objects)
 */

import AbstractModel from './AbstractModel'

export class AbstractPiniaModel extends AbstractModel {
    /**
     * Returns the Pinia store instance.
     * Must be implemented by child classes to return their specific store.
     * 
     * Example:
     * ```
     * static piniaStore() {
     *     return useSubscriptionsStore()
     * }
     * ```
     * 
     * @throws {Error} If not implemented by subclass
     */
    static piniaStore() {
        throw new Error('piniaStore() must be implemented by subclass')
    }

    /**
     * Deprecated: Use piniaStore() instead.
     * Vuex method not available in Pinia models.
     * Throws helpful error directing user to Pinia API.
     * @throws {Error} Always
     */
    static store() {
        throw new Error('AbstractPiniaModel does not use Vuex store(). Use piniaStore() instead.')
    }

    /**
     * Deprecated: Use piniaStore() directly instead.
     * Vuex getters pattern not available in Pinia models.
     * In Pinia, access state directly: store.allSubscriptions (it's already reactive)
     * @throws {Error} Always
     */
    static getters() {
        throw new Error('AbstractPiniaModel does not use Vuex getters(). Access state directly from piniaStore() instead.')
    }

    /**
     * Dispatch/call a Pinia action.
     * 
     * In Pinia, actions are just functions on the store object.
     * Unlike Vuex, there's no explicit dispatch system.
     * 
     * Example:
     * ```
     * Subscription.dispatch('setCurrentSubscriptionById', 5)
     * // internally calls: store.setCurrentSubscriptionById(5)
     * ```
     * 
     * @param {string} action - Action name (method name on the store)
     * @param {*} payload - Payload to pass to the action
     * @returns {*} Result of the action
     * @throws {Error} If action doesn't exist on the store
     */
    static dispatch(action, payload) {
        const store = this.piniaStore()
        if (typeof store[action] === 'function') {
            return store[action](payload)
        }
        throw new Error(`Action "${action}" not found in ${this.name}`)
    }

    /**
     * Access the event bus (mitt instance).
     * 
     * WARNING: Logging a warning but still available (not throwing error).
     * Reason: ebus (mitt) is orthogonal to state management choice.
     * It's valid to use ebus for UI events even with Pinia.
     * 
     * Use sparingly: prefer Pinia store actions for state-related events.
     * Suitable for: UI events, component signals, decoupled notifications.
     * 
     * @returns {EventEmitter} The mitt event bus instance
     */
    static ebus() {
        console.warn('AbstractPiniaModel: ebus() is available but should be used sparingly. Prefer Pinia store actions for state-related events.')
        return window.$_ebus
    }
}

export default AbstractPiniaModel
